/*
 * File: store.ts
 * Project: beaker
 * -----
 * Last Modified: 15 Mar 2022 10:33:40 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { AnyAction, configureStore, ThunkDispatch } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { abbyCadabbyApi, abbyCadabbyParams } from './services/abbyCadabby';
import { animalsApi, animalsConditionsParams, animalsDispositionsParams, animalsOwnersParams } from './services/animals';
import { beautifulDayMonsterApi, beautifulDayMonsterCustomersParams } from './services/beautifulDayMonster';
import { chuckieSueAddressesApi, chuckieSueAddressesParams, chuckieSueWorldsApi } from './services/chuckieSue';
import { userParams } from './services/cookieMonster';
import { cookieMonsterApi } from './services/cookieMonster/cookieMonsterApi';
import { donaldGrumpApi, donaldGrumpProductMastersParams, donaldGrumpReleasedProductsParams } from './services/donaldGrump';
import { elmoApi, elmoDeploymentRequestsParams } from './services/elmo';
import { freddyCadabbyApi, freddyCadabbyParams } from './services/freddyCadabby';
import { juliaApi, juliaProductCatalogParams } from './services/julia';
import { mrsGrouchApi } from './services/mrsGrouch/mrsGrouchApi';
import { rudyCadabbyApi } from './services/rudyCadabby/rudyCadabbyApi';
import { simonSoundmanApi } from './services/simonSoundman/simonSoundmanApi';
import { appSlice, assignedRulesSlice, businessListSlice, formSlice, importSlice, productSearchSlice } from './slices';
import { addressSlice } from './slices/selectedAddressesSlice';
import { selectedBusiness } from './slices/selectedDivisionApplicationsSlice';
import { selectedDivisions } from './slices/selectedDivisionSlice';
import { selectedRoles } from './slices/selectedRoles';

const persistConfig = { key: 'root', storage, version: 1 };

const persistedReducer = persistReducer(persistConfig, businessListSlice.reducer);

export const store = configureStore({
  reducer: {
    [appSlice.name]: appSlice.reducer,
    [formSlice.name]: formSlice.reducer,
    [addressSlice.name]: addressSlice.reducer,
    [productSearchSlice.name]: productSearchSlice.reducer,
    [businessListSlice.name]: persistedReducer,
    [assignedRulesSlice.name]: assignedRulesSlice.reducer,
    [importSlice.name]: importSlice.reducer,
    [selectedBusiness.name]: selectedBusiness.reducer,
    [cookieMonsterApi.reducerPath]: cookieMonsterApi.reducer,
    [userParams.name]: userParams.reducer,
    [selectedRoles.name]: selectedRoles.reducer,
    [selectedDivisions.name]: selectedDivisions.reducer,
    // Elmo
    [elmoApi.reducerPath]: elmoApi.reducer,
    [elmoDeploymentRequestsParams.name]: elmoDeploymentRequestsParams.reducer,
    // Chuckie Sue
    [chuckieSueAddressesApi.reducerPath]: chuckieSueAddressesApi.reducer,
    [chuckieSueAddressesParams.name]: chuckieSueAddressesParams.reducer,
    [chuckieSueWorldsApi.reducerPath]: chuckieSueWorldsApi.reducer,
    // Julia
    [juliaApi.reducerPath]: juliaApi.reducer,
    [juliaProductCatalogParams.name]: juliaProductCatalogParams.reducer,
    // Abby Cadabby
    [abbyCadabbyApi.reducerPath]: abbyCadabbyApi.reducer,
    [abbyCadabbyParams.name]: abbyCadabbyParams.reducer,
    // Freddy Cadabby
    [freddyCadabbyApi.reducerPath]: freddyCadabbyApi.reducer,
    [freddyCadabbyParams.name]: freddyCadabbyParams.reducer,
    // Mrs Grouch
    [mrsGrouchApi.reducerPath]: mrsGrouchApi.reducer,

    // Beautiful Day Monster
    [beautifulDayMonsterApi.reducerPath]: beautifulDayMonsterApi.reducer,
    [beautifulDayMonsterCustomersParams.name]: beautifulDayMonsterCustomersParams.reducer,
    // Donald Grump
    [donaldGrumpApi.reducerPath]: donaldGrumpApi.reducer,
    [donaldGrumpProductMastersParams.name]: donaldGrumpProductMastersParams.reducer,
    [donaldGrumpReleasedProductsParams.name]: donaldGrumpReleasedProductsParams.reducer,
    // Animals
    [animalsApi.reducerPath]: animalsApi.reducer,
    [animalsOwnersParams.name]: animalsOwnersParams.reducer,
    [animalsConditionsParams.name]: animalsConditionsParams.reducer,
    [animalsDispositionsParams.name]: animalsDispositionsParams.reducer,
    // Rudy Cadabby
    [rudyCadabbyApi.reducerPath]: rudyCadabbyApi.reducer,
    // Simon Soundman
    [simonSoundmanApi.reducerPath]: simonSoundmanApi.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      freddyCadabbyApi.middleware,
      elmoApi.middleware,
      chuckieSueAddressesApi.middleware,
      chuckieSueWorldsApi.middleware,
      juliaApi.middleware,
      abbyCadabbyApi.middleware,
      mrsGrouchApi.middleware,
      beautifulDayMonsterApi.middleware,
      donaldGrumpApi.middleware,
      animalsApi.middleware,
      rudyCadabbyApi.middleware,
      cookieMonsterApi.middleware,
      simonSoundmanApi.middleware
    ])
});

export type ReduxState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type ThunkAppDispatch = ThunkDispatch<ReduxState, void, AnyAction>;
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<ReduxState> = useSelector;

setupListeners(store.dispatch);
export default store;

export const persistor = persistStore(store);
